import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronRight, type LucideIcon, Minus, Plus } from 'lucide-react';
import { MarketingHeader } from '@/components/marketing/MarketingHeader';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { Button } from '@/components/ui/button';
import { APP_URL } from '@/data/marketingContent';
import { cn } from '@/lib/utils';
import { CONTENT_REVIEWED, CONTENT_REVIEWED_LABEL } from '@/seo/organization';
import { EASE, Eyebrow, Reveal, SectionHeading, SpotlightCard } from '@/components/home/primitives';
import { SKIP_ENTRANCE } from '@/components/home/motionPrefs';

export { Reveal, SectionHeading };

/* ───────────── Layout ───────────── */

export function PageShell({ children, reviewed = true }: { children: ReactNode; reviewed?: boolean }) {
  return (
    <div className="min-h-screen bg-bv-base text-bv-ink">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-bv-blue focus:px-3 focus:py-2 focus:text-bv-ondeep">Skip to content</a>
      <MarketingHeader />
      <main id="main">
        {children}
        {reviewed && <p className="mx-auto max-w-[1280px] px-5 pb-8 pt-2 text-xs text-bv-mute sm:px-8">Page last reviewed <time dateTime={CONTENT_REVIEWED}>{CONTENT_REVIEWED_LABEL}</time> by the Beatview team.</p>}
      </main>
      <MarketingFooter />
    </div>
  );
}

export function SmartLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  if (/^https?:/.test(href)) return <a href={href} className={className} target={href.includes('beatview.ai') ? undefined : '_blank'} rel={href.includes('beatview.ai') ? undefined : 'noopener noreferrer'}>{children}</a>;
  if (href.includes('#') || /\.(md|csv|pdf)$/.test(href)) return <a href={href} className={className}>{children}</a>;
  return <Link to={href} className={className}>{children}</Link>;
}

export type Crumb = { name: string; href?: string };

export function Breadcrumbs({ items, tone = 'light' }: { items: Crumb[]; tone?: 'light' | 'dark' }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm">
      <ol className={cn('flex flex-wrap items-center gap-1.5', tone === 'light' ? 'text-bv-mute' : 'text-white/50')}>
        <li><Link to="/" className={cn('transition-colors', tone === 'light' ? 'hover:text-bv-ink' : 'hover:text-white')}>Home</Link></li>
        {items.map((item, i) => (
          <li key={item.name} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            {item.href && i < items.length - 1
              ? <SmartLink href={item.href} className={cn('transition-colors', tone === 'light' ? 'hover:text-bv-ink' : 'hover:text-white')}>{item.name}</SmartLink>
              : <span aria-current="page" className={cn('font-medium', tone === 'light' ? 'text-bv-ink' : 'text-white')}>{item.name}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHero({ eyebrow, title, description, children, visual, breadcrumbs, align = 'left' }: { eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; children?: ReactNode; visual?: ReactNode; breadcrumbs?: Crumb[]; align?: 'left' | 'center' }) {
  const reduced = useReducedMotion();
  const enter = (delay: number) => ({ initial: reduced || SKIP_ENTRANCE ? false as const : { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay, ease: EASE } });
  const centered = align === 'center' && !visual;
  return (
    <section className="relative overflow-hidden bg-bv-base pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-44">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="bv-hero-grid absolute inset-0" />
        <div className="absolute -top-56 left-[4%] h-[520px] w-[520px] animate-aurora rounded-full bg-[hsl(var(--blue)/0.14)] blur-[110px]" />
        <div className="absolute -top-32 right-[2%] h-[480px] w-[560px] animate-aurora rounded-full bg-[hsl(var(--hero-highlight-start)/0.15)] blur-[120px] [animation-delay:-7s]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-bv-base" />
      </div>
      <div className={cn('relative mx-auto max-w-[1240px] px-5 lg:px-8', visual && 'grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-12')}>
        <div className={cn('min-w-0', centered ? 'mx-auto max-w-4xl text-center' : !visual && 'max-w-4xl')}>
          {breadcrumbs && <motion.div {...enter(0)} className={cn('mb-7', centered && 'flex justify-center')}><Breadcrumbs items={breadcrumbs} /></motion.div>}
          {eyebrow && <motion.div {...enter(0.03)}><Eyebrow>{eyebrow}</Eyebrow></motion.div>}
          <motion.h1 initial={reduced || SKIP_ENTRANCE ? false : { y: 20 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mt-6 font-display text-[40px] font-semibold leading-[1.02] tracking-[-0.04em] text-bv-ink sm:text-[54px] lg:text-[62px]">{title}</motion.h1>
          {description && <motion.p {...enter(0.12)} className={cn('mt-6 text-[17px] leading-[1.7] text-bv-ink2 sm:text-lg', centered ? 'mx-auto max-w-2xl' : 'max-w-[640px]')}>{description}</motion.p>}
          {children && <motion.div {...enter(0.2)} className="mt-8">{children}</motion.div>}
        </div>
        {visual && <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }} className="min-w-0">{visual}</motion.div>}
      </div>
    </section>
  );
}

const outlineButton = 'h-[54px] rounded-xl border-bv-blue-line bg-white/80 px-6 text-base font-semibold text-bv-ink backdrop-blur hover:bg-white hover:text-bv-ink';

export function HeroActions({ onDemo, primaryLabel = 'Start for free', primaryHref = APP_URL, secondary, note, centered }: { onDemo?: () => void; primaryLabel?: string; primaryHref?: string; secondary?: { label: string; href: string }; note?: ReactNode; centered?: boolean }) {
  return (
    <div className={cn(centered && 'flex flex-col items-center text-center')}>
      <div className={cn('flex flex-col gap-3 sm:flex-row', centered && 'justify-center')}>
        <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-7 text-base font-semibold"><SmartLink href={primaryHref}>{primaryLabel} <ArrowRight className="h-4 w-4" /></SmartLink></Button>
        {onDemo && <Button type="button" size="lg" variant="outline" onClick={onDemo} className={outlineButton}>Book a demo</Button>}
        {secondary && <Button asChild size="lg" variant="outline" className={outlineButton}><SmartLink href={secondary.href}>{secondary.label}</SmartLink></Button>}
      </div>
      {note && <p className="mt-4 text-[14px] font-medium text-bv-ink2">{note}</p>}
    </div>
  );
}

const sectionTones = {
  white: 'bg-white',
  base: 'bg-bv-base',
  wash: 'bg-bv-blue-wash',
  dark: 'relative overflow-hidden bg-bv-ink text-white',
} as const;

export function Section({ tone = 'white', id, labelledBy, className, narrow, children }: { tone?: keyof typeof sectionTones; id?: string; labelledBy?: string; className?: string; narrow?: boolean; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={labelledBy} className={cn('relative py-20 lg:py-28', sectionTones[tone], id && 'scroll-mt-24', className)}>
      {tone === 'dark' && (
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="bv-grid-dark absolute inset-0" />
          <div className="absolute -left-40 top-10 h-[420px] w-[420px] rounded-full bg-[hsl(var(--blue)/0.22)] blur-[120px]" />
          <div className="absolute -right-40 bottom-0 h-[380px] w-[380px] rounded-full bg-[hsl(var(--hero-highlight-end)/0.16)] blur-[120px]" />
        </div>
      )}
      <div className={cn('relative mx-auto px-5 lg:px-8', narrow ? 'max-w-[980px]' : 'max-w-[1240px]')}>{children}</div>
    </section>
  );
}

/* ───────────── Content blocks ───────────── */

/** A quotable, self-contained answer block — written for readers and AI answer engines alike. */
export function InShort({ label = 'In short', children, className }: { label?: string; children: ReactNode; className?: string }) {
  return (
    <Reveal className={className}>
      <div className="rounded-2xl border-l-4 border-bv-coral bg-bv-orange-soft/50 p-6 sm:p-7">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-bv-coral">{label}</p>
        <p className="mt-2 text-lg leading-8 text-bv-ink">{children}</p>
      </div>
    </Reveal>
  );
}

export type CardItem = { icon: LucideIcon; title: string; body: ReactNode; href?: string; link?: string };

export function IconCardGrid({ items, columns = 3, tone = 'light', className }: { items: CardItem[]; columns?: 2 | 3 | 4; tone?: 'light' | 'dark'; className?: string }) {
  const cols = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[columns];
  const light = tone === 'light';
  return (
    <div className={cn('grid gap-5', cols, className)}>
      {items.map(({ icon: Icon, title, body, href, link }, i) => (
        <Reveal key={title} delay={(i % columns) * 0.06}>
          <SpotlightCard tone={tone} className="flex h-full flex-col p-6 sm:p-7">
            <span className={cn('flex h-11 w-11 items-center justify-center rounded-xl transition-colors', light ? 'bg-bv-blue-wash text-bv-blue group-hover:bg-bv-ink group-hover:text-white' : 'bv-btn-gradient text-white')}><Icon className="h-5 w-5" /></span>
            <h3 className={cn('mt-5 font-display text-xl font-semibold tracking-[-0.01em]', light ? 'text-bv-ink' : 'text-white')}>{title}</h3>
            <div className={cn('mt-2 flex-1 text-[15px] leading-7', light ? 'text-bv-ink2' : 'text-white/70')}>{body}</div>
            {href && <SmartLink href={href} className={cn('mt-4 inline-flex items-center gap-1 text-sm font-semibold', light ? 'text-bv-blue' : 'text-bv-blue-bright')}>{link ?? 'Learn more'}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></SmartLink>}
          </SpotlightCard>
        </Reveal>
      ))}
    </div>
  );
}

export type Step = { title: string; body: ReactNode; icon?: LucideIcon };

export function StepFlow({ steps, tone = 'light' }: { steps: Step[]; tone?: 'light' | 'dark' }) {
  const reduced = useReducedMotion();
  const dark = tone === 'dark';
  const lg = steps.length >= 5 ? 'lg:grid-cols-5' : steps.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return (
    <ol className={cn('relative grid gap-8 sm:grid-cols-2', lg)}>
      <motion.span aria-hidden className={cn('absolute left-6 right-6 top-6 hidden h-px origin-left lg:block', dark ? 'bg-white/15' : 'bg-bv-blue-line')} initial={reduced ? false : { scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 1.4, ease: EASE }} />
      {steps.map((step, i) => {
        const Icon = step.icon;
        return (
          <motion.li key={step.title} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ delay: 0.15 + i * 0.12, duration: 0.6, ease: EASE }} className="relative">
            <span className={cn('relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl font-display text-sm font-semibold', dark ? 'bv-btn-gradient text-white shadow-bv-action' : 'border border-bv-blue-line bg-white text-bv-ink shadow-bv-card')}>
              {Icon ? <Icon className="h-5 w-5" /> : `0${i + 1}`}
            </span>
            <p className={cn('mt-5 text-[12px] font-semibold uppercase tracking-wider', dark ? 'text-white/45' : 'text-bv-mute')}>Step 0{i + 1}</p>
            <h3 className={cn('mt-1 font-display text-lg font-semibold leading-snug', dark ? 'text-white' : 'text-bv-ink')}>{step.title}</h3>
            <div className={cn('mt-2 text-[15px] leading-6', dark ? 'text-white/70' : 'text-bv-ink2')}>{step.body}</div>
          </motion.li>
        );
      })}
    </ol>
  );
}

export function CheckList({ items, tone = 'light', icon = 'check', className }: { items: string[]; tone?: 'light' | 'dark'; icon?: 'check' | 'minus'; className?: string }) {
  const Icon = icon === 'check' ? Check : Minus;
  return (
    <ul className={cn('space-y-3', className)}>
      {items.map(item => (
        <li key={item} className="flex gap-3 text-[16px] leading-7">
          <span className={cn('mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full', icon === 'minus' ? 'bg-bv-blue-wash text-bv-mute' : tone === 'light' ? 'bg-bv-success-soft text-bv-success' : 'bg-bv-success text-white')}><Icon className="h-3 w-3" strokeWidth={3} /></span>
          <span className={tone === 'light' ? 'text-bv-ink2' : 'text-white/80'}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function DataTable({ caption, columns, rows, highlight, tone = 'light', minWidth = 720, dense = false }: { caption: string; columns: string[]; rows: ReactNode[][]; highlight?: number; tone?: 'light' | 'dark'; minWidth?: number; dense?: boolean }) {
  const dark = tone === 'dark';
  const pad = dense ? 'px-3.5 py-3' : 'px-5 py-4';
  return (
    <div className={cn('overflow-x-auto rounded-3xl border', dark ? 'border-white/10 bg-white/[0.03]' : 'border-bv-blue-line bg-white shadow-bv-card')} tabIndex={0} role="region" aria-label={caption}>
      <table className={cn('w-full border-collapse text-left', dense ? 'text-[13.5px]' : 'text-[15px]')} style={{ minWidth }}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className={dark ? '' : 'bg-bv-blue-wash/60'}>
            {columns.map((column, i) => (
              <th key={column || i} scope="col" className={cn('relative text-sm font-semibold', pad, dark ? 'text-white/60' : 'text-bv-ink', i === highlight && (dark ? 'bg-white/[0.07] text-white' : 'bg-bv-ink text-white'))}>
                {i === highlight && <span aria-hidden className="bv-btn-gradient absolute inset-x-0 top-0 h-[3px]" />}
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={cn('border-t align-top', dark ? 'border-white/[0.08]' : 'border-bv-blue-line')}>
              {row.map((cell, ci) => ci === 0
                ? <th key={ci} scope="row" className={cn('font-semibold', pad, dark ? 'text-white' : 'text-bv-ink')}>{cell}</th>
                : <td key={ci} className={cn(dense ? 'leading-5' : 'leading-6', pad, dark ? 'text-white/70' : 'text-bv-ink2', ci === highlight && (dark ? 'bg-white/[0.07] font-medium text-white' : 'bg-bv-blue-wash/50 font-medium text-bv-ink'))}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FaqAccordion({ items, idPrefix = 'faq', defaultOpen = 0 }: { items: readonly { question: string; answer: string }[]; idPrefix?: string; defaultOpen?: number | null }) {
  const [open, setOpen] = useState<number | null>(defaultOpen);
  return (
    <div className="border-t border-bv-blue-line">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.question} className="border-b border-bv-blue-line">
            <h3>
              <button type="button" id={`${idPrefix}-q-${i}`} aria-expanded={isOpen} aria-controls={`${idPrefix}-a-${i}`} onClick={() => setOpen(isOpen ? null : i)} className="group flex w-full items-center justify-between gap-6 py-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue focus-visible:ring-offset-4">
                <span className="font-display text-lg font-semibold text-bv-ink transition-colors group-hover:text-bv-blue sm:text-xl">{item.question}</span>
                <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300', isOpen ? 'rotate-45 border-transparent bg-bv-ink text-white' : 'border-bv-blue-line text-bv-ink')}><Plus className="h-4 w-4" /></span>
              </button>
            </h3>
            <motion.div id={`${idPrefix}-a-${i}`} role="region" aria-labelledby={`${idPrefix}-q-${i}`} initial={false} animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.35, ease: EASE }} className="overflow-hidden">
              <p className="max-w-[680px] pb-6 pr-12 text-[16px] leading-7 text-bv-ink2">{item.answer}</p>
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}

export function FaqSection({ title = 'Questions agencies ask before they commit', items, idPrefix, tone = 'white', aside }: { title?: string; items: readonly { question: string; answer: string }[]; idPrefix?: string; tone?: 'white' | 'base' | 'wash'; aside?: ReactNode }) {
  return (
    <Section tone={tone} labelledBy={`${idPrefix ?? 'faq'}-title`}>
      <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <SectionHeading id={`${idPrefix ?? 'faq'}-title`} eyebrow="FAQ" title={title} />
          <Reveal delay={0.1}>
            {aside ?? (
              <div className="mt-8 rounded-3xl border border-bv-blue-line bg-bv-blue-wash/60 p-6">
                <p className="font-display text-lg font-semibold text-bv-ink">Still deciding?</p>
                <p className="mt-2 text-[15px] leading-6 text-bv-ink2">Bring a live vacancy and we’ll walk through where automation ends and recruiter review begins.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild className="h-11 rounded-xl px-5"><Link to="/contact">Book a demo</Link></Button>
                  <Button asChild variant="outline" className="h-11 rounded-xl border-bv-blue-line bg-white px-5 text-bv-ink hover:bg-white hover:text-bv-blue"><Link to="/faq">See all questions</Link></Button>
                </div>
              </div>
            )}
          </Reveal>
        </div>
        <Reveal delay={0.05}><FaqAccordion items={items} idPrefix={idPrefix} /></Reveal>
      </div>
    </Section>
  );
}

export function WindowFrame({ label = 'platform.beatview.ai', example = true, children, className, bodyClassName }: { label?: string; example?: boolean; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-[24px] border border-white bg-white shadow-[0_40px_100px_-50px_hsl(var(--navy)/0.5)] ring-1 ring-bv-blue-line', className)}>
      <div className="flex items-center gap-3 border-b border-bv-blue-line bg-bv-blue-wash/70 px-4 py-3">
        <div className="flex gap-1.5" aria-hidden><span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" /></div>
        <span className="min-w-0 truncate rounded-md border border-bv-blue-line bg-white px-3 py-1 text-xs font-medium text-bv-mute">{label}</span>
        {example && <span className="ml-auto shrink-0 rounded-md bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Example data</span>}
      </div>
      <div className={cn('p-4 sm:p-6', bodyClassName)}>{children}</div>
    </div>
  );
}

export function VisualStage({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded-[30px] border border-bv-blue-line bg-bv-blue-wash/60 p-6 sm:p-10', className)}>
      <div aria-hidden className="bv-hero-grid absolute inset-0 opacity-70" />
      <div className="relative">{children}</div>
    </div>
  );
}

export type LinkCard = { title: string; body: string; href: string; icon: LucideIcon };

export function RelatedLinks({ eyebrow = 'Keep exploring', title, items, tone = 'white' }: { eyebrow?: string; title: string; items: LinkCard[]; tone?: 'white' | 'base' | 'wash' }) {
  const cols = items.length >= 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : items.length === 3 ? 'md:grid-cols-3' : 'sm:grid-cols-2';
  return (
    <Section tone={tone}>
      <SectionHeading eyebrow={eyebrow} title={title} />
      <div className={cn('mt-12 grid gap-4', cols)}>
        {items.map(({ title: cardTitle, body, href, icon: Icon }, i) => (
          <Reveal key={href + cardTitle} delay={i * 0.06}>
            <SmartLink href={href} className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-bv-blue-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-bv-window focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
              <span aria-hidden className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[hsl(var(--hero-highlight-start))] to-[hsl(var(--hero-highlight-end))] transition-transform duration-500 group-hover:scale-x-100" />
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue transition-colors group-hover:bg-bv-ink group-hover:text-white"><Icon className="h-5 w-5" /></span>
              <h3 className="mt-6 font-display text-xl font-semibold text-bv-ink">{cardTitle}</h3>
              <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{body}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
            </SmartLink>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

export function BillingToggle({ annual, onChange }: { annual: boolean; onChange: (annual: boolean) => void }) {
  return (
    <div role="group" aria-label="Billing period" className="inline-flex rounded-full border border-bv-blue-line bg-white p-1 shadow-bv-card">
      {([['Monthly', false], ['Annual', true]] as const).map(([label, value]) => {
        const selected = annual === value;
        return (
          <button key={label} type="button" aria-pressed={selected} onClick={() => onChange(value)} className="relative rounded-full px-5 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
            {selected && <motion.span layoutId="page-billing-pill" className="absolute inset-0 rounded-full bg-bv-ink" transition={{ type: 'spring', stiffness: 420, damping: 32 }} />}
            <span className={cn('relative inline-flex items-center gap-1.5', selected ? 'text-white' : 'text-bv-ink2')}>
              {label}
              {value && <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', selected ? 'bg-bv-success text-white' : 'bg-bv-success-soft text-bv-success')}>Save 18%</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ───────────── Closing call to action ───────────── */

export function CtaPanel({ eyebrow = 'Bring a live vacancy', title, description, onDemo, primaryLabel = 'Start for free', primaryHref = APP_URL, secondary, bullets = ['Free plan with one active role', 'Runs alongside your ATS', 'Recruiters keep every decision'] }: { eyebrow?: string; title: ReactNode; description?: ReactNode; onDemo?: () => void; primaryLabel?: string; primaryHref?: string; secondary?: { label: string; href: string }; bullets?: string[] }) {
  const ghost = 'h-[54px] rounded-xl border-white/20 bg-white/5 px-8 text-base font-semibold text-white backdrop-blur hover:bg-white/10 hover:text-white';
  return (
    <section className="bg-bv-base px-3 pb-6 pt-10 sm:px-5 lg:pb-10" aria-label="Get started with Beatview">
      <div className="relative mx-auto max-w-[1280px] overflow-hidden rounded-[36px] bg-bv-ink px-6 py-20 text-center text-white sm:px-12 lg:py-24">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="bv-grid-dark absolute inset-0" />
          <div className="absolute left-1/2 top-[-40%] h-[140%] w-[140%] -translate-x-1/2 animate-spin-slow rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,hsl(var(--hero-highlight-start)/0.2)_60deg,transparent_120deg,hsl(var(--blue)/0.2)_220deg,transparent_300deg)] blur-3xl [animation-duration:24s]" />
          <div className="absolute bottom-[-30%] left-1/2 h-[360px] w-[640px] -translate-x-1/2 rounded-full bg-[hsl(var(--hero-highlight-end)/0.32)] blur-[120px]" />
        </div>
        <div className="relative">
          <Reveal><Eyebrow tone="dark" live>{eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mx-auto mt-7 max-w-4xl font-display text-[36px] font-semibold leading-[1.04] tracking-[-0.04em] sm:text-5xl lg:text-6xl">{title}</h2></Reveal>
          {description && <Reveal delay={0.1}><p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/70">{description}</p></Reveal>}
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-8 text-base font-semibold"><SmartLink href={primaryHref}>{primaryLabel} <ArrowRight className="h-4 w-4" /></SmartLink></Button>
              {onDemo && <Button type="button" size="lg" variant="outline" onClick={onDemo} className={ghost}>Book a demo</Button>}
              {secondary && <Button asChild size="lg" variant="outline" className={ghost}><SmartLink href={secondary.href}>{secondary.label}</SmartLink></Button>}
            </div>
          </Reveal>
          {bullets.length > 0 && (
            <Reveal delay={0.2}>
              <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/60">
                {bullets.map(item => <li key={item} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-bv-success" />{item}</li>)}
              </ul>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
